/**
 * PostStreakModal — celebracyjny popup po osiągnięciu streak'u publikacji postów.
 *
 * Trigger: PostCreatorV2 wywołuje setStreakInfo({streak, isNew}) gdy streak >= 3.
 *
 * UX: confetti, gradient violet-fuchsia, liczbnik dni, CTA "Zaplanuj kolejny post".
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, X, CalendarClock, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { firePawConfetti } from '@/utils/confetti';

const STREAK_TIERS = {
  3: {
    title: '3 dni z rzędu publikujesz!',
    subtitle: 'Twoja hodowla jest 2× bardziej widoczna.',
    insight: 'Algorytmy Facebooka faworyzują regularne profile — utrzymuj rytm!',
    emoji: '🔥',
  },
  7: {
    title: 'Tydzień postów z rzędu!',
    subtitle: 'Wpadłeś w rytm — twoja społeczność rośnie szybciej.',
    insight: 'Hodowle z 7-dniowym streak\'iem dostają 3× więcej zapytań.',
    emoji: '⚡',
  },
  14: {
    title: '2 tygodnie regularności!',
    subtitle: 'Jesteś w top 5% aktywnych hodowców APISAI.',
    insight: 'Twoja widoczność w Google rośnie z każdym dniem.',
    emoji: '👑',
  },
  30: {
    title: '30 dni z rzędu!',
    subtitle: 'Legenda APISAI — twoja marka hodowli jest nie do podrobienia.',
    insight: 'Najbardziej aktywne hodowle generują 10× więcej leadów.',
    emoji: '🏆',
  },
};

function pickTier(streak) {
  if (streak >= 30) return STREAK_TIERS[30];
  if (streak >= 14) return STREAK_TIERS[14];
  if (streak >= 7) return STREAK_TIERS[7];
  return STREAK_TIERS[3];
}

export default function PostStreakModal({ streak, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    try { firePawConfetti(); } catch {}
  }, []);

  if (!streak || streak < 3) return null;

  const tier = pickTier(streak);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 animate-in fade-in duration-200" data-testid="post-streak-modal">
      <button
        type="button"
        aria-label="Zamknij"
        onClick={onClose}
        className="absolute inset-0 bg-gray-900/70 backdrop-blur-sm"
      />

      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
        <button
          type="button"
          onClick={onClose}
          data-testid="post-streak-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 hover:bg-gray-100 flex items-center justify-center text-gray-500 z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Hero gradient */}
        <div className="relative px-8 pt-10 pb-8 bg-gradient-to-br from-orange-500 via-pink-500 to-fuchsia-600 text-white text-center overflow-hidden">
          <div className="absolute -top-12 -right-12 w-44 h-44 bg-white/15 rounded-full blur-2xl" />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-yellow-300/20 rounded-full blur-2xl" />

          <div className="relative">
            <div className="text-7xl mb-3 leading-none">{tier.emoji}</div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur rounded-full text-xs font-bold mb-3 uppercase tracking-wider">
              <Flame className="w-3.5 h-3.5" />
              Streak {streak} {streak === 1 ? 'dzień' : 'dni'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold leading-tight" data-testid="post-streak-title">
              {tier.title}
            </h2>
            <p className="text-sm sm:text-base text-white/90 mt-2 max-w-sm mx-auto">
              {tier.subtitle}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6 space-y-4">
          <div className="flex gap-3 p-4 rounded-xl bg-violet-50 border border-violet-100">
            <Sparkles className="w-5 h-5 text-violet-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700 leading-relaxed">{tier.insight}</p>
          </div>

          {/* Streak dots visualization */}
          <div className="flex items-center justify-center gap-1.5 py-2">
            {Array.from({ length: Math.min(streak, 14) }).map((_, i) => (
              <div
                key={i}
                className="w-2.5 h-2.5 rounded-full bg-gradient-to-br from-orange-400 to-pink-500"
                style={{ animationDelay: `${i * 50}ms` }}
              />
            ))}
            {streak > 14 && (
              <span className="text-xs text-gray-500 font-semibold ml-2">+{streak - 14}</span>
            )}
          </div>

          <Button
            type="button"
            onClick={() => {
              onClose();
              navigate('/panel/kreator-postu');
            }}
            data-testid="post-streak-schedule-btn"
            className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:opacity-95 text-white shadow-lg shadow-violet-200/60"
          >
            <CalendarClock className="w-4 h-4 mr-1.5" />
            Zaplanuj kolejny post — utrzymaj rytm
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>

          <p className="text-xs text-gray-400 text-center">
            Streak zliczany od pierwszego dnia publikacji.
          </p>
        </div>
      </div>
    </div>
  );
}
